import { useCallback, useEffect, useRef, useState } from 'react';
import type {
  WorkflowTriggerNotification,
  OrderWorkflowTriggerNotification,
} from '../types/connector.types';
import { useConnector } from '../contexts/ConnectorContext';
import { errorLogger } from '../utils/errorLogger';

const LAST_SEEN_KEY   = 'notifications_last_seen_at';
const MAX_TOASTS      = 3;
const POLL_INTERVAL   = 60_000; // 60 s

export interface NotificationToast {
  id: string;
  kind: 'workflow' | 'order';
  /** actionType for workflow, triggerType for order — used for colour coding */
  subType: string;
  title: string;
  detail: string;
  created: string;
}

export interface UseNotificationsResult {
  workflowItems: WorkflowTriggerNotification[];
  orderItems: OrderWorkflowTriggerNotification[];
  unseenCount: number;
  totalCount: number;
  isLoading: boolean;
  error: Error | null;
  hideWorkflow: (id: string) => Promise<void>;
  hideOrder: (id: string) => Promise<void>;
  refresh: () => Promise<void>;
  markSeen: () => Promise<void>;
  toasts: NotificationToast[];
  dismissToast: (id: string) => void;
}

// ─── Humanise helpers ────────────────────────────────────────────────────────

function humaniseWorkflow(n: WorkflowTriggerNotification): string {
  const month = formatPeriodMonth(n.periodMonth);
  switch (n.actionType) {
    case 'SUBMIT_MONTH':        return `${n.userName} submitted their timesheet for ${month}`;
    case 'APPROVE_MONTH':       return `${n.approverName} approved ${n.userName}'s timesheet for ${month}`;
    case 'REJECT_MONTH':        return `${n.approverName} rejected ${n.userName}'s timesheet for ${month}`;
    case 'SUPER_APPROVE_MONTH': return `${n.approverName} super-approved ${n.userName}'s timesheet for ${month}`;
    case 'LOCK_MONTH':          return `${n.userName}'s timesheet for ${month} was locked`;
    case 'UNLOCK_MONTH':        return `${n.userName}'s timesheet for ${month} was unlocked`;
    case 'SEND_CHASE_EMAIL':    return `Chase email sent to ${n.userName} for ${month}`;
    default:                    return n.title || n.actionType;
  }
}

function humaniseOrder(n: OrderWorkflowTriggerNotification): string {
  switch (n.triggerType) {
    case 'ORDER_SUBMITTED':            return `${n.requesterName} submitted order ${n.orderRef}`;
    case 'LM_APPROVAL_REQUEST':        return `Approval requested from ${n.recipientName} for ${n.orderRef}`;
    case 'CHEMICAL_APPROVAL_REQUEST':  return `Chemical approval requested for ${n.orderRef}`;
    case 'ESCALATED_APPROVAL_REQUEST': return `Escalated approval requested for ${n.orderRef}`;
    case 'CEO_APPROVAL_REQUEST':       return `CEO approval requested for ${n.orderRef}`;
    case 'CFO_APPROVAL_REQUEST':       return `CFO approval requested for ${n.orderRef}`;
    case 'ORDER_APPROVED_TO_OM':       return `Order ${n.orderRef} approved and sent to Order Management`;
    case 'ORDER_SELF_APPROVED_TO_OM':  return `Order ${n.orderRef} self-approved`;
    case 'ORDER_REJECTED':             return `${n.recipientName} rejected order ${n.orderRef}`;
    case 'ORDER_QUERIED':              return `${n.recipientName} queried order ${n.orderRef}`;
    case 'ORDER_PLACED':               return `Order ${n.orderRef} has been placed`;
    default:                           return n.title || n.triggerType;
  }
}

function formatPeriodMonth(pm: string): string {
  if (!pm || !pm.includes('-')) return pm;
  const [year, month] = pm.split('-').map(Number);
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${months[month - 1] ?? month} ${year}`;
}

export { humaniseWorkflow, humaniseOrder, formatPeriodMonth };

// ─── Dedup ───────────────────────────────────────────────────────────────────

// Items must be sorted newest-first; first occurrence of each key wins.
function dedupeLatest<T>(items: T[], keyFn: (item: T) => string): T[] {
  const seen = new Set<string>();
  const result: T[] = [];
  for (const item of items) {
    const key = keyFn(item);
    if (!seen.has(key)) { seen.add(key); result.push(item); }
  }
  return result;
}

// ─── Hook ────────────────────────────────────────────────────────────────────

export function useNotifications(userEmail: string): UseNotificationsResult {
  const connector = useConnector();
  const [workflowItems, setWorkflowItems] = useState<WorkflowTriggerNotification[]>([]);
  const [orderItems,    setOrderItems]    = useState<OrderWorkflowTriggerNotification[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error,     setError]     = useState<Error | null>(null);
  const [toasts,    setToasts]    = useState<NotificationToast[]>([]);

  // ISO string of the latest Created field seen — poll cursor
  const cursorRef         = useRef<string | null>(null);
  // Guard so on-load toasts fire only once per mount
  const toastsShownRef    = useRef(false);

  // ── Helpers ──────────────────────────────────────────────────────────────

  const getLastSeen = (): Date | null => {
    const v = localStorage.getItem(LAST_SEEN_KEY);
    return v ? new Date(v) : null;
  };

  /** Advance cursor to the max Created seen in this batch. */
  const advanceCursor = (wf: WorkflowTriggerNotification[], ord: OrderWorkflowTriggerNotification[]) => {
    const all = [...wf.map(n => n.created), ...ord.map(n => n.created)];
    if (all.length === 0) return;
    const latest = all.reduce((a, b) => (a > b ? a : b));
    if (!cursorRef.current || latest > cursorRef.current) cursorRef.current = latest;
  };

  /** Push up to MAX_TOASTS new toasts, skipping any IDs already shown. */
  const pushToasts = useCallback((
    newWf:  WorkflowTriggerNotification[],
    newOrd: OrderWorkflowTriggerNotification[],
  ) => {
    const entries: NotificationToast[] = [
      ...newWf.map(n => ({
        id:      `wf-${n.id}`,
        kind:    'workflow' as const,
        subType: n.actionType,
        title:   humaniseWorkflow(n),
        detail:  `${n.source ? n.source + ' · ' : ''}${formatPeriodMonth(n.periodMonth)}`,
        created: n.created,
      })),
      ...newOrd.map(n => ({
        id:      `ord-${n.id}`,
        kind:    'order' as const,
        subType: n.triggerType,
        title:   humaniseOrder(n),
        detail:  n.orderTitle ? `${n.orderTitle} · £${n.orderAmount.toLocaleString()}` : n.orderRef,
        created: n.created,
      })),
    ].slice(0, MAX_TOASTS);

    if (entries.length === 0) return;
    setToasts(prev => {
      const existing = new Set(prev.map(t => t.id));
      const fresh = entries.filter(t => !existing.has(t.id));
      return [...fresh, ...prev].slice(0, MAX_TOASTS);
    });
  }, []);

  // ── Initial full load ────────────────────────────────────────────────────

  const load = useCallback(async (showOnLoadToasts = false) => {
    if (!userEmail) return;
    setIsLoading(true);
    setError(null);
    try {
      const [wf, ord] = await Promise.all([
        connector.getWorkflowTriggerNotifications({ userEmail, limit: 200 }),
        connector.getOrderWorkflowTriggerNotifications({ userEmail, limit: 200 }),
      ]);

      advanceCursor(wf, ord);

      const visWf  = dedupeLatest(wf.filter(n => !n.hideNotification),  n => `${n.userEmail}|${n.periodMonth}`);
      const visOrd = dedupeLatest(ord.filter(n => !n.hideNotification), n => String(n.orderId));
      setWorkflowItems(visWf);
      setOrderItems(visOrd);

      if (showOnLoadToasts && !toastsShownRef.current) {
        toastsShownRef.current = true;
        const lastSeen = getLastSeen();
        if (lastSeen) {
          const newWf  = visWf.filter(n => new Date(n.created) > lastSeen).slice(0, MAX_TOASTS);
          const newOrd = visOrd.filter(n => new Date(n.created) > lastSeen).slice(0, MAX_TOASTS - newWf.length);
          pushToasts(newWf, newOrd);
        } else {
          // First ever visit — record now as baseline; no toasts yet
          localStorage.setItem(LAST_SEEN_KEY, new Date().toISOString());
        }
      }
    } catch (err) {
      const e = err instanceof Error ? err : new Error(String(err));
      errorLogger.log(e, { component: 'useNotifications', action: 'load' });
      setError(e);
    } finally {
      setIsLoading(false);
    }
  }, [connector, userEmail, pushToasts]);

  // ── Background poll ──────────────────────────────────────────────────────

  const poll = useCallback(async () => {
    if (!userEmail || !cursorRef.current) return;

    const since = cursorRef.current;
    try {
      const [wf, ord] = await Promise.all([
        connector.getWorkflowTriggerNotifications({ userEmail, since, limit: 50 }),
        connector.getOrderWorkflowTriggerNotifications({ userEmail, since, limit: 50 }),
      ]);

      // Strictly newer than cursor (query uses `ge` so the boundary item comes back again)
      const freshWf  = wf.filter(n => n.created > since && !n.hideNotification);
      const freshOrd = ord.filter(n => n.created > since && !n.hideNotification);

      advanceCursor(wf, ord);

      if (freshWf.length === 0 && freshOrd.length === 0) return;

      // Merge: new items first so dedup keeps the latest action per timesheet/order
      setWorkflowItems(prev =>
        dedupeLatest([...freshWf, ...prev], n => `${n.userEmail}|${n.periodMonth}`)
      );
      setOrderItems(prev =>
        dedupeLatest([...freshOrd, ...prev], n => String(n.orderId))
      );

      pushToasts(freshWf, freshOrd);
    } catch {
      // Poll failures are silent — don't interrupt the user
    }
  }, [connector, userEmail, pushToasts]);

  // ── Effects ──────────────────────────────────────────────────────────────

  useEffect(() => { if (userEmail) void load(true); }, [load, userEmail]);

  useEffect(() => {
    if (!userEmail) return;
    const id = setInterval(() => { void poll(); }, POLL_INTERVAL);
    return () => clearInterval(id);
  }, [poll, userEmail]);

  // ── Actions ──────────────────────────────────────────────────────────────

  const hideWorkflow = useCallback(async (id: string) => {
    await connector.hideWorkflowTriggerNotification(id);
    setWorkflowItems(prev => prev.filter(n => n.id !== id));
  }, [connector]);

  const hideOrder = useCallback(async (id: string) => {
    await connector.hideOrderWorkflowTriggerNotification(id);
    setOrderItems(prev => prev.filter(n => n.id !== id));
  }, [connector]);

  const dismissToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  const markSeen = useCallback(async () => {
    const unreadWf  = workflowItems.filter(n => !n.read).map(n => n.id);
    const unreadOrd = orderItems.filter(n => !n.read).map(n => n.id);

    // Optimistic update so the badge clears immediately
    if (unreadWf.length > 0)
      setWorkflowItems(prev => prev.map(n => unreadWf.includes(n.id) ? { ...n, read: true } : n));
    if (unreadOrd.length > 0)
      setOrderItems(prev => prev.map(n => unreadOrd.includes(n.id) ? { ...n, read: true } : n));

    // Persist to SharePoint in the background
    try {
      await Promise.all([
        unreadWf.length  > 0 ? connector.markWorkflowTriggerNotificationsRead(unreadWf)  : Promise.resolve(),
        unreadOrd.length > 0 ? connector.markOrderTriggerNotificationsRead(unreadOrd) : Promise.resolve(),
      ]);
    } catch {
      // Silent — optimistic state is already correct; SP will catch up on next poll
    }

    localStorage.setItem(LAST_SEEN_KEY, new Date().toISOString());
  }, [connector, workflowItems, orderItems]);

  // ── Unseen count (SP Read field) ─────────────────────────────────────────

  const unseenCount = workflowItems.filter(n => !n.read).length +
                      orderItems.filter(n => !n.read).length;

  const totalCount = workflowItems.length + orderItems.length;

  return {
    workflowItems,
    orderItems,
    unseenCount,
    totalCount,
    isLoading,
    error,
    hideWorkflow,
    hideOrder,
    refresh: () => load(false),
    markSeen,
    toasts,
    dismissToast,
  };
}
